import React, { memo } from 'react';
import { useTranslation } from 'react-i18next';
import { NeuralNetworkParamsProps } from './types';
import { ACTIVATION_FUNCTIONS } from './constants';
import InfoTooltip from '@/shared/components/forms/InfoTooltip';

const inputBase = 'w-full border rounded-lg px-2.5 py-1.5 sm:px-3 sm:py-2 text-xs sm:text-sm text-foreground bg-card disabled:bg-muted disabled:text-muted-foreground transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-ring/30 focus:border-ring [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none';
const inputValid = 'border-input';
const inputError = 'border-red-300 bg-red-50/30';

/**
 * Neural Network Parameters sub-component
 * Displays configuration options for Dense, CNN, LSTM, AR LSTM models
 */
export const NeuralNetworkParams: React.FC<NeuralNetworkParamsProps> = memo(({
  modelParams,
  disabled,
  validationErrors,
  onParameterChange,
  onValidationErrorChange
}) => {
  const { t } = useTranslation('training');

  const handleLayerChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    const numVal = val ? parseInt(val) : undefined;

    // Validate range
    if (numVal && (numVal < 1 || numVal > 10)) {
      onValidationErrorChange(prev => ({ ...prev, LAY: t('training.validationErrors.layerRange') }));
    } else {
      onValidationErrorChange(prev => {
        const newErrors = { ...prev };
        delete newErrors.LAY;
        return newErrors;
      });
    }

    onParameterChange('LAY', numVal);
  };

  const hasLayError = !!validationErrors.LAY || !modelParams.LAY;

  // Tooltip key helpers per model
  const layerTooltip = modelParams.MODE === 'CNN'
    ? t('tooltips.models.cnn.layerCount')
    : modelParams.MODE === 'LSTM' || modelParams.MODE === 'AR LSTM'
      ? t('tooltips.models.lstm.layerCount')
      : t('tooltips.models.dense.layerCount');

  const neuronsTooltip = modelParams.MODE === 'CNN'
    ? t('tooltips.models.cnn.filtersPerLayer')
    : modelParams.MODE === 'LSTM' || modelParams.MODE === 'AR LSTM'
      ? t('tooltips.models.lstm.neuronsPerLayer')
      : t('tooltips.models.dense.neuronsPerLayer');

  const epochsTooltip = modelParams.MODE === 'Dense'
    ? t('tooltips.models.dense.epochs')
    : t('tooltips.models.cnn.epochs');

  const activationTooltip = modelParams.MODE === 'Dense'
    ? t('tooltips.models.dense.activation')
    : t('tooltips.models.cnn.activation');

  // Per-activation-value tooltips
  const getActivationValueTooltip = (actf: string) => {
    const map: Record<string, string> = {
      ReLU: t('tooltips.activations.relu'),
      Sigmoid: t('tooltips.activations.sigmoid'),
      Tanh: t('tooltips.activations.tanh'),
      Linear: t('tooltips.activations.linear'),
      Softmax: t('tooltips.activations.softmax'),
      None: t('tooltips.activations.none'),
    };
    return map[actf] ?? '';
  };
  const hasNError = !modelParams.N;
  const hasKError = !modelParams.K;
  const hasEPError = !modelParams.EP;
  const hasACTFError = !modelParams.ACTF;

  return (
    <>
      {/* Number of Layers */}
      <div className="grid grid-cols-[2fr_3fr] sm:grid-cols-[1fr_2fr] items-start gap-x-3 px-4 py-3 sm:px-6 sm:py-3.5">
        <div className="flex items-center gap-1 pt-1.5 sm:pt-2">
          <label
            htmlFor="model-config-LAY"
            className="text-[10px] sm:text-xs uppercase tracking-wide font-medium text-muted-foreground"
          >
            {t('training.modelConfiguration.layers')}
            {!modelParams.LAY && <span className="text-red-400 ml-0.5">*</span>}
          </label>
          <InfoTooltip text={layerTooltip} width="w-80" />
        </div>
        <div>
          <input
            id="model-config-LAY"
            type="number"
            value={modelParams.LAY || ''}
            onChange={handleLayerChange}
            disabled={disabled}
            className={`${inputBase} ${hasLayError ? inputError : inputValid}`}
            min="1"
            max="10"
            placeholder={t('training.modelConfiguration.placeholders.enterNumber')}
          />
          {validationErrors.LAY ? (
            <p className="text-red-500 text-[10px] mt-1">{validationErrors.LAY}</p>
          ) : !modelParams.LAY ? (
            <p className="text-red-500 text-[10px] mt-1">
              {t('training.validation.required', 'Required')}
            </p>
          ) : null}
        </div>
      </div>

      {/* Neurons per Layer / Filters */}
      <div className="grid grid-cols-[2fr_3fr] sm:grid-cols-[1fr_2fr] items-start gap-x-3 px-4 py-3 sm:px-6 sm:py-3.5">
        <div className="flex items-center gap-1 pt-1.5 sm:pt-2">
          <label
            htmlFor="model-config-N"
            className="text-[10px] sm:text-xs uppercase tracking-wide font-medium text-muted-foreground"
          >
            {modelParams.MODE === 'CNN' ? t('training.modelConfiguration.filters') : t('training.modelConfiguration.neurons')}
            {!modelParams.N && <span className="text-red-400 ml-0.5">*</span>}
          </label>
          <InfoTooltip text={neuronsTooltip} width="w-80" />
        </div>
        <div>
          <input
            id="model-config-N"
            type="number"
            value={modelParams.N || ''}
            onChange={(e) => {
              const val = e.target.value;
              onParameterChange('N', val ? parseInt(val) : undefined);
            }}
            disabled={disabled}
            className={`${inputBase} ${hasNError ? inputError : inputValid}`}
            min="1"
            max="2048"
            step="32"
            placeholder={t('training.modelConfiguration.placeholders.enterNumber')}
          />
          {!modelParams.N && (
            <p className="text-red-500 text-[10px] mt-1">
              {t('training.validation.required', 'Required')}
            </p>
          )}
        </div>
      </div>

      {/* Kernel Size for CNN */}
      {modelParams.MODE === 'CNN' && (
        <div className="grid grid-cols-[2fr_3fr] sm:grid-cols-[1fr_2fr] items-start gap-x-3 px-4 py-3 sm:px-6 sm:py-3.5">
          <div className="flex items-center gap-1 pt-1.5 sm:pt-2">
            <label
              htmlFor="model-config-K"
              className="text-[10px] sm:text-xs uppercase tracking-wide font-medium text-muted-foreground"
            >
              {t('training.modelConfiguration.kernelSize')}
              {!modelParams.K && <span className="text-red-400 ml-0.5">*</span>}
            </label>
            <InfoTooltip text={t('tooltips.models.cnn.kernelSize')} width="w-80" />
          </div>
          <div>
            <input
              id="model-config-K"
              type="number"
              value={modelParams.K || ''}
              onChange={(e) => {
                const val = e.target.value;
                onParameterChange('K', val ? parseInt(val) : undefined);
              }}
              disabled={disabled}
              className={`${inputBase} ${hasKError ? inputError : inputValid}`}
              min="1"
              max="11"
              step="2"
              placeholder={t('training.modelConfiguration.placeholders.enterSize')}
            />
            {!modelParams.K && (
              <p className="text-red-500 text-[10px] mt-1">
                {t('training.validation.required', 'Required')}
              </p>
            )}
          </div>
        </div>
      )}

      {/* Max Epochs */}
      <div className="grid grid-cols-[2fr_3fr] sm:grid-cols-[1fr_2fr] items-start gap-x-3 px-4 py-3 sm:px-6 sm:py-3.5">
        <div className="flex items-center gap-1 pt-1.5 sm:pt-2">
          <label
            htmlFor="model-config-EP"
            className="text-[10px] sm:text-xs uppercase tracking-wide font-medium text-muted-foreground"
          >
            {t('training.modelConfiguration.maxEpochs')}
            {!modelParams.EP && <span className="text-red-400 ml-0.5">*</span>}
          </label>
          <InfoTooltip text={epochsTooltip} width="w-80" />
        </div>
        <div>
          <input
            id="model-config-EP"
            type="number"
            value={modelParams.EP || ''}
            onChange={(e) => {
              const val = e.target.value;
              onParameterChange('EP', val ? parseInt(val) : undefined);
            }}
            disabled={disabled}
            className={`${inputBase} ${hasEPError ? inputError : inputValid}`}
            min="1"
            max="1000"
            placeholder={t('training.modelConfiguration.placeholders.enterNumber')}
          />
          {!modelParams.EP && (
            <p className="text-red-500 text-[10px] mt-1">
              {t('training.validation.required', 'Required')}
            </p>
          )}
        </div>
      </div>

      {/* Activation Function */}
      <div className="grid grid-cols-[2fr_3fr] sm:grid-cols-[1fr_2fr] items-start gap-x-3 px-4 py-3 sm:px-6 sm:py-3.5">
        <div className="flex items-center gap-1 pt-1.5 sm:pt-2">
          <label
            htmlFor="model-config-ACTF"
            className="text-[10px] sm:text-xs uppercase tracking-wide font-medium text-muted-foreground"
          >
            {t('training.modelConfiguration.activationFunction')}
            {!modelParams.ACTF && <span className="text-red-400 ml-0.5">*</span>}
          </label>
          <InfoTooltip text={activationTooltip} width="w-80" />
        </div>
        <div>
          <div className="flex items-center gap-2">
            <select
              id="model-config-ACTF"
              value={modelParams.ACTF || ''}
              onChange={(e) => onParameterChange('ACTF', e.target.value)}
              disabled={disabled}
              className={`w-full border rounded-lg px-2.5 py-1.5 sm:px-3 sm:py-2 text-xs sm:text-sm text-foreground bg-card disabled:bg-muted disabled:text-muted-foreground transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-ring/30 focus:border-ring ${
                hasACTFError ? inputError : inputValid
              }`}
            >
              <option value="">{t('training.modelConfiguration.placeholders.selectActivation')}</option>
              {ACTIVATION_FUNCTIONS.map((func) => (
                <option key={func} value={func}>
                  {func}
                </option>
              ))}
            </select>
            {modelParams.ACTF && <InfoTooltip text={getActivationValueTooltip(modelParams.ACTF)} width="w-72" />}
          </div>
          {!modelParams.ACTF && (
            <p className="text-red-500 text-[10px] mt-1">
              {t('training.validation.required', 'Required')}
            </p>
          )}
        </div>
      </div>
    </>
  );
});

NeuralNetworkParams.displayName = 'NeuralNetworkParams';

export default NeuralNetworkParams;
